import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import MetaTags from "react-meta-tags";
import {
  Modal,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import axios from "axios";
import swal from "sweetalert";
import "./AssetList.scss";

const ASSET_ICONS = {
  Domain: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Subdomain: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8" />
    </svg>
  ),
  IP: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  ),
  "Mobile Application": (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  ),
  "Source Code": (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  ),
};

const CATEGORIES = [
  { key: "Domain", label: "Domain" },
  { key: "Subdomain", label: "Subdomain" },
  { key: "IP", label: "IP" },
  { key: "Mobile Application", label: "Mobile Application" },
  { key: "Source Code", label: "Source Code" },
];

const normFeature = (item) => {
  if (!item) return "Domain";
  const f = String(item.Feature || item.Type || "").toLowerCase().replace(/[\s_-]/g, "");
  if (f === "domain") return "Domain";
  if (f === "subdomain") return "Subdomain";
  if (f === "ip") return "IP";
  if (f.includes("mobile") || f.includes("apk") || f.includes("application")) return "Mobile Application";
  if (f.includes("source") || f.includes("code")) return "Source Code";
  if (item.Domain) return "Domain";
  if (item.Subdomain) return "Subdomain";
  if (item.IP) return "IP";
  if (item.ApplicationName || (item.FileName && item.FileName.endsWith(".apk"))) return "Mobile Application";
  if (item.ProjectName || (item.FileName && item.FileName.endsWith(".rar"))) return "Source Code";
  return "Domain";
};

const getAssetValue = (item) => {
  if (!item) return "-";
  return (
    item.Domain ||
    item.Subdomain ||
    item.IP ||
    item.ApplicationName ||
    item.ProjectName ||
    item.Value ||
    item.FileName ||
    "-"
  );
};

const AssetList = () => {
  const orgID = localStorage.getItem("orgID");
  const token = localStorage.getItem("token");

  const { layoutMode } = useSelector((state) => state.Layout || { layoutMode: "dark" });
  const theme = layoutMode === "light" ? "light" : "dark";

  // Data states
  const [productData, setProductData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter, Search, Sort, Pagination
  const [activeType, setActiveType] = useState(null); // null = All
  const [searchQuery, setSearchQuery] = useState("");
  const [sortKey, setSortKey] = useState("id");
  const [sortDir, setSortDir] = useState(1); // 1 = asc, -1 = desc
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRows, setSelectedRows] = useState({});

  // Export State
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Modals
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [modalPopupData, setModalPopupData] = useState([]);

  // Add Asset modal flow
  const [assetTypeModalOpen, setAssetTypeModalOpen] = useState(false);
  const [addAssetStep2Open, setAddAssetStep2Open] = useState(false);
  const [addAssetType, setAddAssetType] = useState("Domain");
  const [domainValue, setDomainValue] = useState("");
  const [subdomainValue, setSubdomainValue] = useState("");
  const [ipValue, setIpValue] = useState("");
  const [applicationName, setApplicationName] = useState("");
  const [applicationPlatform, setApplicationPlatform] = useState("APK");
  const [projectName, setProjectName] = useState("");
  const [projectLanguage, setProjectLanguage] = useState("C");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSavingAsset, setIsSavingAsset] = useState(false);

  // Check auth & fetch initial data
  useEffect(() => {
    let isMounted = true;
    async function checkAuthAndFetch() {
      try {
        const resp = await fetch(`${process.env.REACT_APP_DEFAULTPATH}jwtauth`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = await resp.json();
        if (!data.status && isMounted) {
          window.location.href = "/login";
          return;
        }
      } catch (err) {
        console.warn("Auth check warning:", err);
      }
      if (isMounted) {
        getassetListData();
      }
    }
    checkAuthAndFetch();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch asset inventory from backend
  const getassetListData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_DEFAULTPATH}getassetlist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ orgID }),
      });
      const data = await response.json();
      if (data && Array.isArray(data.assetList)) {
        setProductData(data.assetList);
      } else {
        setProductData([]);
      }
    } catch (err) {
      console.error("Failed to load asset inventory:", err);
      setProductData([]);
    } finally {
      setLoading(false);
    }
  };

  // Counts by category
  const counts = useMemo(() => {
    const c = {
      total: productData.length,
      Domain: 0,
      Subdomain: 0,
      IP: 0,
      "Mobile Application": 0,
      "Source Code": 0,
    };
    productData.forEach((item) => {
      const type = normFeature(item);
      if (c[type] !== undefined) {
        c[type]++;
      }
    });
    return c;
  }, [productData]);

  // Filtered and Sorted Rows
  const filteredRows = useMemo(() => {
    let result = productData.filter((item) => {
      const type = normFeature(item);
      if (activeType && type !== activeType) return false;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const assetName = String(getAssetValue(item)).toLowerCase();
        const id = String(item.ID || item.id || "").toLowerCase();
        const source = String(item.Source || "").toLowerCase();
        const status = String(item.Status || "").toLowerCase();
        const itemType = String(type).toLowerCase();
        return (
          assetName.includes(q) ||
          id.includes(q) ||
          source.includes(q) ||
          status.includes(q) ||
          itemType.includes(q)
        );
      }
      return true;
    });

    if (sortKey) {
      result = [...result].sort((a, b) => {
        let valA = "";
        let valB = "";
        if (sortKey === "id") {
          valA = String(a.ID || a.id || "");
          valB = String(b.ID || b.id || "");
        } else if (sortKey === "type") {
          valA = normFeature(a);
          valB = normFeature(b);
        } else if (sortKey === "asset") {
          valA = String(getAssetValue(a));
          valB = String(getAssetValue(b));
        } else if (sortKey === "source") {
          valA = String(a.Source || "");
          valB = String(b.Source || "");
        } else if (sortKey === "status") {
          valA = String(a.Status || "");
          valB = String(b.Status || "");
        }
        return valA.localeCompare(valB, undefined, { numeric: true }) * sortDir;
      });
    }

    return result;
  }, [productData, activeType, searchQuery, sortKey, sortDir]);

  // Pagination calculation
  const totalRows = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const currentPageSafe = Math.min(currentPage, totalPages);
  const startIdx = (currentPageSafe - 1) * pageSize;
  const paginatedRows = filteredRows.slice(startIdx, startIdx + pageSize);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDir(1);
    }
  };

  // Row selection handlers
  const handleToggleRow = (rowId) => {
    setSelectedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleToggleAll = () => {
    const allSelectedOnPage =
      paginatedRows.length > 0 && paginatedRows.every((r) => selectedRows[r.ID || r.id]);
    const updated = { ...selectedRows };
    if (allSelectedOnPage) {
      paginatedRows.forEach((r) => {
        delete updated[r.ID || r.id];
      });
    } else {
      paginatedRows.forEach((r) => {
        updated[r.ID || r.id] = true;
      });
    }
    setSelectedRows(updated);
  };

  const selectedCount = useMemo(() => {
    return Object.values(selectedRows).filter(Boolean).length;
  }, [selectedRows]);

  const clearSelection = () => {
    setSelectedRows({});
  };

  // Export full inventory via API
  const handleExportAPI = async (format, e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (e && e.stopPropagation) e.stopPropagation();
    setIsExporting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_DEFAULTPATH}assetinventory/export?format=${format}&orgID=${orgID}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "organization-id": orgID || "",
          },
        }
      );

      if (!response.ok) {
        let errMsg = "Failed to export assets";
        try {
          const errJson = await response.json();
          if (errJson.error) errMsg = errJson.error;
        } catch (_) {}
        throw new Error(errMsg);
      }

      const rawBlob = await response.blob();
      const blob = new Blob([rawBlob], {
        type: format === "csv" ? "text/csv;charset=utf-8;" : "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;

      const disposition = response.headers.get("content-disposition");
      let filename = `asset_inventory_${orgID || "assets"}.${format}`;
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename=["']?([^"';]+)["']?/);
        if (match && match[1]) filename = match[1];
      }

      a.download = filename;
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        a.remove();
        window.URL.revokeObjectURL(url);
      }, 1500);

      swal("Success", `Asset inventory exported as ${format.toUpperCase()} successfully!`, "success");
    } catch (err) {
      swal("Export Failed", err.message || "Failed to export assets", "error");
    } finally {
      setIsExporting(false);
      setExportDropdownOpen(false);
    }
  };

  // Export only selected rows as CSV client-side
  const handleExportSelectedCSV = () => {
    const selectedItems = productData.filter((item) => selectedRows[item.ID || item.id]);
    if (!selectedItems.length) return;

    const headers = ["ID", "Type", "Asset", "Source", "Status", "DateTime"];
    const rows = selectedItems.map((item) => [
      `"${item.ID || item.id || ""}"`,
      `"${normFeature(item)}"`,
      `"${String(getAssetValue(item)).replace(/"/g, '""')}"`,
      `"${item.Source || "Manual"}"`,
      `"${item.Status || "Verified"}"`,
      `"${item.DateTime || ""}"`,
    ]);
    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `selected_assets_${orgID || "export"}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      a.remove();
      window.URL.revokeObjectURL(url);
    }, 1500);
    swal("Success", `${selectedItems.length} selected assets exported to CSV!`, "success");
  };

  // View details modal trigger
  const handleViewMore = (item) => {
    const assetVal = getAssetValue(item);
    setModalPopupData([
      {
        ID: item.ID || item.id || "-",
        Type: normFeature(item),
        Value: assetVal,
        Source: item.Source || "Manual",
        Status: item.Status || "Verified",
        DateTime: item.DateTime || "-",
      },
    ]);
    setViewModalOpen(true);
  };

  // Add Asset modal submit
  const handleSaveAsset = async () => {
    setIsSavingAsset(true);
    try {
      if (addAssetType === "Domain") {
        const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
        if (!domainRegex.test(domainValue.trim())) {
          swal("Validation Error", "Please enter a valid domain (e.g. example.com)!", "error");
          setIsSavingAsset(false);
          return;
        }

        await axios.post(`${process.env.REACT_APP_DEFAULTPATH}saveAssetList-scalable`, {
          orgID,
          DomainValue: domainValue.trim(),
          addAssetType: "Domain",
        });

        swal("Success", "Domain added to Asset Inventory successfully!", "success");
        setAddAssetStep2Open(false);
        setDomainValue("");
        getassetListData();
      } else if (addAssetType === "Subdomain") {
        const subdomainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        if (!subdomainRegex.test(subdomainValue.trim())) {
          swal("Validation Error", "Please enter a valid subdomain (e.g. api.example.com)!", "error");
          setIsSavingAsset(false);
          return;
        }

        await axios.post(`${process.env.REACT_APP_DEFAULTPATH}saveAssetList-scalable`, {
          orgID,
          SubdomainValue: subdomainValue.trim(),
          addAssetType: "Subdomain",
        });

        swal("Success", "Subdomain added to Asset Inventory successfully!", "success");
        setAddAssetStep2Open(false);
        setSubdomainValue("");
        getassetListData();
      } else if (addAssetType === "IP") {
        const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        if (!ipRegex.test(ipValue.trim())) {
          swal("Validation Error", "Please enter a valid IP address (e.g. 192.168.1.1)!", "error");
          setIsSavingAsset(false);
          return;
        }

        await axios.post(`${process.env.REACT_APP_DEFAULTPATH}saveAssetList-scalable`, {
          orgID,
          IPValue: ipValue.trim(),
          addAssetType: "IP",
        });

        swal("Success", "IP Address added to Asset Inventory successfully!", "success");
        setAddAssetStep2Open(false);
        setIpValue("");
        getassetListData();
      } else if (addAssetType === "APK") {
        if (!uploadedFile || !uploadedFile[0]) {
          swal("Missing File", "Please select an APK file to upload!", "warning");
          setIsSavingAsset(false);
          return;
        }

        const formData = new window.FormData();
        formData.append("apkFile", uploadedFile[0]);
        formData.append("addAssetType", "APK");
        formData.append("applicationPlatform", applicationPlatform);
        formData.append("applicationName", applicationName.trim() || uploadedFile[0].name);
        formData.append("orgID", orgID);

        await axios.post(`${process.env.REACT_APP_DEFAULTPATH}saveAssetList-scalable`, formData);

        swal("Success", "Mobile Application added successfully!", "success");
        setAddAssetStep2Open(false);
        setApplicationName("");
        setUploadedFile(null);
        getassetListData();
      } else if (addAssetType === "SourceCode") {
        if (!uploadedFile || !uploadedFile[0]) {
          swal("Missing File", "Please select a source code archive (.rar or .zip)!", "warning");
          setIsSavingAsset(false);
          return;
        }

        const formData = new window.FormData();
        formData.append("apkFile", uploadedFile[0]);
        formData.append("addAssetType", "SourceCode");
        formData.append("projectLanguage", projectLanguage);
        formData.append("projectName", projectName.trim() || uploadedFile[0].name);
        formData.append("orgID", orgID);

        await axios.post(`${process.env.REACT_APP_DEFAULTPATH}saveAssetList-scalable`, formData);

        swal("Success", "Source Code asset added successfully!", "success");
        setAddAssetStep2Open(false);
        setProjectName("");
        setUploadedFile(null);
        getassetListData();
      }
    } catch (err) {
      console.error("Save Asset Error:", err);
      swal("Error", err.response?.data?.message || err.message || "Failed to save asset", "error");
    } finally {
      setIsSavingAsset(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content asset-inventory-root" data-theme={theme}>
        <MetaTags>
          <title>Asset Inventory | Glogix</title>
        </MetaTags>

        <div className="asset-inventory-container">
          {/* Header Row: Breadcrumbs, Title, Actions */}
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div className="asset-breadcrumb">
                <Link to="/dashboard">Dashboard</Link>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m9 18 6-6-6-6" />
                </svg>
                <span className="current">Asset Inventory</span>
              </div>
              <h1 className="asset-title">Asset Inventory</h1>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              {/* Export Dropdown */}
              <Dropdown isOpen={exportDropdownOpen} toggle={() => setExportDropdownOpen((prev) => !prev)}>
                <DropdownToggle
                  tag="button"
                  className="asset-btn btn-secondary"
                  disabled={isExporting}
                  id="btn-export-assets-dropdown"
                >
                  {isExporting ? (
                    <>
                      <Spinner size="sm" style={{ width: "12px", height: "12px", borderWidth: "2px" }} />
                      <span>Exporting...</span>
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                      </svg>
                      <span>Export</span>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </>
                  )}
                </DropdownToggle>
                <DropdownMenu
                  end
                  style={{
                    background: "var(--bg-surface)",
                    borderColor: "var(--border-default)",
                    borderRadius: "var(--radius-md)",
                    boxShadow: "var(--shadow-lg)",
                    minWidth: "160px",
                    padding: "6px",
                  }}
                >
                  <DropdownItem
                    onClick={(e) => handleExportAPI("csv", e)}
                    style={{
                      color: "var(--fg-primary)",
                      borderRadius: "var(--radius-sm)",
                      fontSize: "13px",
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      background: "transparent",
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--green-500)" strokeWidth="2" strokeLinecap="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    Export as CSV
                  </DropdownItem>
                  <DropdownItem
                    onClick={(e) => handleExportAPI("json", e)}
                    style={{
                      color: "var(--fg-primary)",
                      borderRadius: "var(--radius-sm)",
                      fontSize: "13px",
                      padding: "8px 12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      background: "transparent",
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--amber-500)" strokeWidth="2" strokeLinecap="round">
                      <polyline points="16 18 22 12 16 6" />
                      <polyline points="8 6 2 12 8 18" />
                    </svg>
                    Export as JSON
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>

              {/* Add Asset Button */}
              <button
                className="asset-btn btn-primary"
                onClick={() => setAssetTypeModalOpen(true)}
                id="btn-add-asset"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                <span>Add Asset</span>
              </button>
            </div>
          </div>

          {/* Top KPI Card Strip: Total + 5 Categories */}
          <div className="asset-kpi-strip">
            <div className="asset-kpi-total">
              <span className="kpi-label">Total assets</span>
              <span className="kpi-value">{counts.total}</span>
            </div>

            {CATEGORIES.map((cat) => {
              const isActive = activeType === cat.key;
              const countVal = counts[cat.key] || 0;
              return (
                <div
                  key={cat.key}
                  className={`asset-kpi-item ${isActive ? "active" : ""}`}
                  onClick={() => {
                    setActiveType(isActive ? null : cat.key);
                    setCurrentPage(1);
                  }}
                  title={`Filter by ${cat.label}`}
                >
                  <div className="kpi-icon-wrap" style={{ color: countVal > 0 ? "var(--blue-500)" : "var(--fg-tertiary)" }}>
                    {ASSET_ICONS[cat.key]}
                  </div>
                  <div className="kpi-text-wrap">
                    <span className="kpi-num" style={{ color: countVal > 0 ? "var(--fg-primary)" : "var(--fg-tertiary)" }}>
                      {countVal}
                    </span>
                    <span className="kpi-desc">{cat.label}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Asset Table Container */}
          <section className="asset-table-card">
            {/* Search & Filter Bar */}
            <div className="asset-controls-bar">
              <div className="asset-search-input-box">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--fg-tertiary)" strokeWidth="2" strokeLinecap="round">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" />
                </svg>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </div>

              <div className="asset-divider" />

              {/* Filter Chips */}
              <div className="asset-filter-chips">
                <button
                  type="button"
                  className={`asset-chip ${activeType === null ? "active" : ""}`}
                  onClick={() => {
                    setActiveType(null);
                    setCurrentPage(1);
                  }}
                >
                  <span>All</span>
                  <span className="chip-count">{counts.total}</span>
                </button>

                {CATEGORIES.map((cat) => {
                  const isActive = activeType === cat.key;
                  const cVal = counts[cat.key] || 0;
                  return (
                    <button
                      type="button"
                      key={cat.key}
                      className={`asset-chip ${isActive ? "active" : ""}`}
                      onClick={() => {
                        setActiveType(isActive ? null : cat.key);
                        setCurrentPage(1);
                      }}
                    >
                      <span>{cat.label}</span>
                      <span className="chip-count">{cVal}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selection Action Bar */}
            {selectedCount > 0 && (
              <div className="asset-selection-bar">
                <span className="selection-count">{selectedCount} selected</span>
                <button
                  type="button"
                  className="asset-btn"
                  style={{ height: "26px", fontSize: "12px", padding: "0 10px" }}
                  onClick={handleExportSelectedCSV}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                  </svg>
                  Export Selected (CSV)
                </button>
                <button
                  type="button"
                  className="asset-action-link"
                  style={{ color: "var(--fg-secondary)", fontSize: "12px" }}
                  onClick={clearSelection}
                >
                  Clear
                </button>
              </div>
            )}

            {/* Data Table */}
            <div className="asset-table-wrap">
              <table className="asset-table">
                <thead>
                  <tr>
                    <th style={{ width: "36px" }}>
                      <input
                        type="checkbox"
                        checked={paginatedRows.length > 0 && paginatedRows.every((r) => selectedRows[r.ID || r.id])}
                        onChange={handleToggleAll}
                        style={{ accentColor: "var(--brand-primary)", cursor: "pointer" }}
                      />
                    </th>
                    <th className="sortable" onClick={() => handleSort("asset")}>
                      ASSET {sortKey === "asset" && (sortDir === 1 ? "↑" : "↓")}
                    </th>
                    <th className="sortable" onClick={() => handleSort("type")}>
                      TYPE {sortKey === "type" && (sortDir === 1 ? "↑" : "↓")}
                    </th>
                    <th className="sortable" onClick={() => handleSort("source")}>
                      SOURCE {sortKey === "source" && (sortDir === 1 ? "↑" : "↓")}
                    </th>
                    <th className="sortable" onClick={() => handleSort("status")}>
                      STATUS {sortKey === "status" && (sortDir === 1 ? "↑" : "↓")}
                    </th>
                    <th className="sortable" onClick={() => handleSort("id")}>
                      ID {sortKey === "id" && (sortDir === 1 ? "↑" : "↓")}
                    </th>
                    <th style={{ textAlign: "right" }}>VIEW DETAILS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center", padding: "40px", color: "var(--fg-secondary)" }}>
                        <Spinner size="sm" className="me-2" />
                        Loading assets...
                      </td>
                    </tr>
                  ) : paginatedRows.length === 0 ? (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center", padding: "48px 16px", color: "var(--fg-secondary)" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--fg-tertiary)" strokeWidth="1.5">
                            <rect x="3" y="3" width="7" height="7" />
                            <rect x="14" y="3" width="7" height="7" />
                            <rect x="14" y="14" width="7" height="7" />
                            <rect x="3" y="14" width="7" height="7" />
                          </svg>
                          <span style={{ fontSize: "13px", fontWeight: "500" }}>No assets found</span>
                          <span style={{ fontSize: "12px", color: "var(--fg-tertiary)" }}>
                            {searchQuery ? "Try refining your search keyword" : "Add assets to get started"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedRows.map((item, idx) => {
                      const rowId = item.ID || item.id || `row-${idx}`;
                      const isSelected = !!selectedRows[rowId];
                      const itemType = normFeature(item);
                      const assetVal = getAssetValue(item);
                      const sourceVal = item.Source || "Manual";
                      const statusVal = item.Status || "Verified";

                      return (
                        <tr key={rowId} className={isSelected ? "selected-row" : ""}>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleRow(rowId)}
                              style={{ accentColor: "var(--brand-primary)", cursor: "pointer" }}
                            />
                          </td>
                          <td>
                            <span className="asset-mono-val">{assetVal}</span>
                          </td>
                          <td>
                            <span className="asset-type-badge">
                              {ASSET_ICONS[itemType] || ASSET_ICONS["Domain"]}
                              <span>{itemType}</span>
                            </span>
                          </td>
                          <td>
                            <span className="asset-source-tag">{sourceVal}</span>
                          </td>
                          <td>
                            <span className="asset-status-tag">
                              <span className="status-dot" />
                              <span>{statusVal}</span>
                            </span>
                          </td>
                          <td>
                            <span className="asset-id-tag">{item.ID || item.id || "-"}</span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <button
                              type="button"
                              className="asset-action-link"
                              onClick={() => handleViewMore(item)}
                            >
                              View More
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="asset-pagination-bar">
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span>
                  Showing {totalRows > 0 ? startIdx + 1 : 0} to{" "}
                  {Math.min(startIdx + pageSize, totalRows)} of {totalRows} assets
                </span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    height: "26px",
                    background: "var(--bg-surface-sunken)",
                    color: "var(--fg-primary)",
                    border: "1px solid var(--border-default)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "11px",
                    padding: "0 4px",
                    cursor: "pointer",
                  }}
                >
                  <option value={10}>10 / page</option>
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <button
                  type="button"
                  className="asset-page-btn"
                  disabled={currentPageSafe <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  title="Previous page"
                >
                  ‹
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => {
                    if (totalPages <= 7) return true;
                    if (p === 1 || p === totalPages) return true;
                    return Math.abs(p - currentPageSafe) <= 1;
                  })
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) {
                      acc.push("...");
                    }
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((item, idx) =>
                    item === "..." ? (
                      <span key={`dots-${idx}`} style={{ padding: "0 4px", color: "var(--fg-tertiary)" }}>
                        …
                      </span>
                    ) : (
                      <button
                        type="button"
                        key={item}
                        className={`asset-page-btn ${item === currentPageSafe ? "active" : ""}`}
                        onClick={() => setCurrentPage(item)}
                      >
                        {item}
                      </button>
                    )
                  )}
                <button
                  type="button"
                  className="asset-page-btn"
                  disabled={currentPageSafe >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  title="Next page"
                >
                  ›
                </button>
              </div>
            </div>
          </section>
        </div>

        {/* View Details Modal */}
        <Modal
          isOpen={viewModalOpen}
          toggle={() => setViewModalOpen(false)}
          className={`asset-modal-dialog ${theme === "light" ? "theme-light" : "theme-dark"} modal-lg`}
          contentClassName="asset-modal-content"
          centered
        >
          <div
            className="modal-header"
            style={{
              backgroundColor: theme === "light" ? "#ffffff" : "#111a2b",
              borderBottom: theme === "light" ? "1px solid #dfe5ee" : "1px solid #26324a",
              padding: "16px 20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  width: "30px",
                  height: "30px",
                  borderRadius: "6px",
                  background: "rgba(47, 111, 237, 0.15)",
                  color: "#2f6fed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {modalPopupData[0] ? ASSET_ICONS[modalPopupData[0].Type] || ASSET_ICONS["Domain"] : ASSET_ICONS["Domain"]}
              </div>
              <h5
                className="modal-title"
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: 600,
                  color: theme === "light" ? "#0f172a" : "#ffffff",
                }}
              >
                Asset Details
              </h5>
            </div>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setViewModalOpen(false)}
              aria-label="Close"
              style={{
                background: "transparent",
                border: "none",
                color: theme === "light" ? "#64748b" : "#8695ab",
                fontSize: "22px",
                lineHeight: 1,
                cursor: "pointer",
                padding: "2px 6px",
              }}
            >
              ×
            </button>
          </div>

          <div
            className="modal-body"
            style={{
              backgroundColor: theme === "light" ? "#ffffff" : "#111a2b",
              color: theme === "light" ? "#0f172a" : "#e6ebf2",
              padding: "20px",
            }}
          >
            {modalPopupData[0] && (
              <>
                {/* Hero Asset Summary Header */}
                <div
                  className="asset-detail-hero"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "14px",
                    padding: "14px 18px",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    background: theme === "light" ? "#f8fafc" : "#141d30",
                    border: theme === "light" ? "1px solid #dfe5ee" : "1px solid #26324a",
                    flexWrap: "wrap",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                    <div
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "8px",
                        background: "rgba(47, 111, 237, 0.15)",
                        color: "#2f6fed",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      {ASSET_ICONS[modalPopupData[0].Type] || ASSET_ICONS["Domain"]}
                    </div>
                    <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "15px",
                          fontWeight: 700,
                          color: theme === "light" ? "#0f172a" : "#ffffff",
                          wordBreak: "break-all",
                        }}
                      >
                        {modalPopupData[0].Value}
                      </span>
                      <span style={{ fontSize: "11px", color: "var(--fg-secondary)" }}>
                        ID: {modalPopupData[0].ID}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        padding: "3px 9px",
                        borderRadius: "999px",
                        background: "rgba(31, 157, 87, 0.15)",
                        color: "#1f9d57",
                        fontSize: "11px",
                        fontWeight: 600,
                      }}
                    >
                      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#1f9d57" }} />
                      {modalPopupData[0].Status}
                    </span>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "3px 9px",
                        borderRadius: "999px",
                        background: "rgba(47, 111, 237, 0.12)",
                        color: "#4a86f5",
                        fontSize: "11px",
                        fontWeight: 600,
                      }}
                    >
                      {modalPopupData[0].Type}
                    </span>
                  </div>
                </div>

                {/* 2-Column Key-Value Properties Grid */}
                <div
                  className="asset-detail-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                    gap: "12px",
                  }}
                >
                  <div
                    className="asset-detail-card"
                    style={{
                      padding: "12px 14px",
                      borderRadius: "6px",
                      background: theme === "light" ? "#f8fafc" : "#141d30",
                      border: theme === "light" ? "1px solid #dfe5ee" : "1px solid #26324a",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <span className="detail-lbl" style={{ fontSize: "11px", color: "var(--fg-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
                      Asset Target
                    </span>
                    <span className="detail-val mono" style={{ fontSize: "13px", fontFamily: "var(--font-mono)", fontWeight: 600, color: theme === "light" ? "#0f172a" : "#e6ebf2", wordBreak: "break-all" }}>
                      {modalPopupData[0].Value}
                    </span>
                  </div>

                  <div
                    className="asset-detail-card"
                    style={{
                      padding: "12px 14px",
                      borderRadius: "6px",
                      background: theme === "light" ? "#f8fafc" : "#141d30",
                      border: theme === "light" ? "1px solid #dfe5ee" : "1px solid #26324a",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <span className="detail-lbl" style={{ fontSize: "11px", color: "var(--fg-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
                      Asset ID
                    </span>
                    <span className="detail-val mono" style={{ fontSize: "13px", fontFamily: "var(--font-mono)", color: theme === "light" ? "#0f172a" : "#e6ebf2" }}>
                      {modalPopupData[0].ID}
                    </span>
                  </div>

                  <div
                    className="asset-detail-card"
                    style={{
                      padding: "12px 14px",
                      borderRadius: "6px",
                      background: theme === "light" ? "#f8fafc" : "#141d30",
                      border: theme === "light" ? "1px solid #dfe5ee" : "1px solid #26324a",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <span className="detail-lbl" style={{ fontSize: "11px", color: "var(--fg-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
                      Discovery Source
                    </span>
                    <span className="detail-val mono" style={{ fontSize: "13px", fontFamily: "var(--font-mono)", color: theme === "light" ? "#0f172a" : "#e6ebf2" }}>
                      {modalPopupData[0].Source}
                    </span>
                  </div>

                  <div
                    className="asset-detail-card"
                    style={{
                      padding: "12px 14px",
                      borderRadius: "6px",
                      background: theme === "light" ? "#f8fafc" : "#141d30",
                      border: theme === "light" ? "1px solid #dfe5ee" : "1px solid #26324a",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <span className="detail-lbl" style={{ fontSize: "11px", color: "var(--fg-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
                      Discovered / Added Date
                    </span>
                    <span className="detail-val" style={{ fontSize: "13px", color: theme === "light" ? "#0f172a" : "#e6ebf2" }}>
                      {modalPopupData[0].DateTime}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          <div
            className="modal-footer"
            style={{
              backgroundColor: theme === "light" ? "#f8fafc" : "#141d30",
              borderTop: theme === "light" ? "1px solid #dfe5ee" : "1px solid #26324a",
              padding: "12px 20px",
            }}
          >
            <button
              type="button"
              className="asset-btn btn-secondary"
              onClick={() => setViewModalOpen(false)}
            >
              Close
            </button>
          </div>
        </Modal>

        {/* Step 1: Add Asset - Choose Asset Type */}
        <Modal
          isOpen={assetTypeModalOpen}
          toggle={() => setAssetTypeModalOpen(false)}
          className={`asset-modal-dialog ${theme === "light" ? "theme-light" : "theme-dark"}`}
          contentClassName="asset-modal-content"
          centered
        >
          <div
            className="modal-header"
            style={{
              backgroundColor: theme === "light" ? "#ffffff" : "#111a2b",
              borderBottom: theme === "light" ? "1px solid #dfe5ee" : "1px solid #26324a",
              padding: "16px 20px",
            }}
          >
            <h5 className="modal-title" style={{ color: theme === "light" ? "#0f172a" : "#ffffff", margin: 0 }}>
              Add Asset - Select Category
            </h5>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setAssetTypeModalOpen(false)}
              aria-label="Close"
              style={{
                background: "transparent",
                border: "none",
                color: theme === "light" ? "#64748b" : "#8695ab",
                fontSize: "22px",
                lineHeight: 1,
                cursor: "pointer",
                padding: "2px 6px",
              }}
            >
              ×
            </button>
          </div>
          <div
            className="modal-body"
            style={{
              backgroundColor: theme === "light" ? "#ffffff" : "#111a2b",
              color: theme === "light" ? "#0f172a" : "#e6ebf2",
              padding: "20px",
            }}
          >
            <p style={{ margin: "0 0 16px", fontSize: "13px", color: "var(--fg-secondary)" }}>
              Choose the category of asset you want to monitor in your attack surface inventory:
            </p>
            <div className="asset-type-cards-grid">
              {[
                { type: "Domain", label: "Domain", icon: ASSET_ICONS["Domain"] },
                { type: "Subdomain", label: "Subdomain", icon: ASSET_ICONS["Subdomain"] },
                { type: "IP", label: "IP Address", icon: ASSET_ICONS["IP"] },
                { type: "APK", label: "Mobile App", icon: ASSET_ICONS["Mobile Application"] },
                { type: "SourceCode", label: "Source Code", icon: ASSET_ICONS["Source Code"] },
              ].map((c) => (
                <button
                  type="button"
                  key={c.type}
                  className={`asset-type-card-btn ${addAssetType === c.type ? "active" : ""}`}
                  onClick={() => setAddAssetType(c.type)}
                >
                  <div style={{ width: "24px", height: "24px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {c.icon}
                  </div>
                  <span>{c.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div
            className="modal-footer"
            style={{
              backgroundColor: theme === "light" ? "#f8fafc" : "#141d30",
              borderTop: theme === "light" ? "1px solid #dfe5ee" : "1px solid #26324a",
              padding: "12px 20px",
            }}
          >
            <button
              type="button"
              className="asset-btn btn-secondary"
              onClick={() => setAssetTypeModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="asset-btn btn-primary"
              onClick={() => {
                setAssetTypeModalOpen(false);
                setAddAssetStep2Open(true);
              }}
            >
              Next
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
        </Modal>

        {/* Step 2: Add Asset - Enter Details */}
        <Modal
          isOpen={addAssetStep2Open}
          toggle={() => setAddAssetStep2Open(false)}
          className={`asset-modal-dialog ${theme === "light" ? "theme-light" : "theme-dark"}`}
          contentClassName="asset-modal-content"
          centered
        >
          <div
            className="modal-header"
            style={{
              backgroundColor: theme === "light" ? "#ffffff" : "#111a2b",
              borderBottom: theme === "light" ? "1px solid #dfe5ee" : "1px solid #26324a",
              padding: "16px 20px",
            }}
          >
            <h5 className="modal-title" style={{ color: theme === "light" ? "#0f172a" : "#ffffff", margin: 0 }}>
              {addAssetType === "Domain" && "Add Domain Asset"}
              {addAssetType === "Subdomain" && "Add Subdomain Asset"}
              {addAssetType === "IP" && "Add IP Address Asset"}
              {addAssetType === "APK" && "Upload Mobile Application"}
              {addAssetType === "SourceCode" && "Add Source Code Project"}
            </h5>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setAddAssetStep2Open(false)}
              aria-label="Close"
              style={{
                background: "transparent",
                border: "none",
                color: theme === "light" ? "#64748b" : "#8695ab",
                fontSize: "22px",
                lineHeight: 1,
                cursor: "pointer",
                padding: "2px 6px",
              }}
            >
              ×
            </button>
          </div>
          <div
            className="modal-body"
            style={{
              backgroundColor: theme === "light" ? "#ffffff" : "#111a2b",
              color: theme === "light" ? "#0f172a" : "#e6ebf2",
              padding: "20px",
            }}
          >
            {addAssetType === "Domain" && (
              <div className="asset-form-group">
                <label>Domain Name</label>
                <input
                  type="text"
                  className="asset-form-input"
                  placeholder="example.com"
                  value={domainValue}
                  onChange={(e) => setDomainValue(e.target.value)}
                  autoFocus
                />
                <span style={{ fontSize: "11px", color: "var(--fg-tertiary)", marginTop: "4px", display: "block" }}>
                  Enter a root organizational domain without protocol or slashes (e.g., amenify.com)
                </span>
              </div>
            )}

            {addAssetType === "Subdomain" && (
              <div className="asset-form-group">
                <label>Subdomain FQDN</label>
                <input
                  type="text"
                  className="asset-form-input"
                  placeholder="api.example.com"
                  value={subdomainValue}
                  onChange={(e) => setSubdomainValue(e.target.value)}
                  autoFocus
                />
                <span style={{ fontSize: "11px", color: "var(--fg-tertiary)", marginTop: "4px", display: "block" }}>
                  Enter a fully qualified subdomain (e.g., api.amenify.com)
                </span>
              </div>
            )}

            {addAssetType === "IP" && (
              <div className="asset-form-group">
                <label>IPv4 Address</label>
                <input
                  type="text"
                  className="asset-form-input"
                  placeholder="198.185.159.144"
                  value={ipValue}
                  onChange={(e) => setIpValue(e.target.value)}
                  autoFocus
                />
                <span style={{ fontSize: "11px", color: "var(--fg-tertiary)", marginTop: "4px", display: "block" }}>
                  Enter a public or routable IPv4 address
                </span>
              </div>
            )}

            {addAssetType === "APK" && (
              <>
                <div className="asset-form-group">
                  <label>Application Name</label>
                  <input
                    type="text"
                    className="asset-form-input"
                    placeholder="e.g. Amenify Customer App"
                    value={applicationName}
                    onChange={(e) => setApplicationName(e.target.value)}
                  />
                </div>
                <div className="asset-form-group">
                  <label>Platform</label>
                  <select
                    className="asset-form-select"
                    value={applicationPlatform}
                    onChange={(e) => setApplicationPlatform(e.target.value)}
                  >
                    <option value="APK">Android (APK)</option>
                    <option value="IPK">iOS / WebOS (IPK)</option>
                  </select>
                </div>
                <div className="asset-form-group">
                  <label>Application Binary (.apk)</label>
                  <label className="asset-file-drop">
                    <input
                      type="file"
                      accept=".apk"
                      onChange={(e) => setUploadedFile(e.target.files)}
                    />
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--blue-500)" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                      </svg>
                      <span style={{ fontSize: "13px", color: "var(--fg-primary)", fontWeight: "500" }}>
                        {uploadedFile && uploadedFile[0] ? uploadedFile[0].name : "Choose an .apk file"}
                      </span>
                      <span style={{ fontSize: "11px", color: "var(--fg-tertiary)" }}>
                        Click to browse application package
                      </span>
                    </div>
                  </label>
                </div>
              </>
            )}

            {addAssetType === "SourceCode" && (
              <>
                <div className="asset-form-group">
                  <label>Project Name</label>
                  <input
                    type="text"
                    className="asset-form-input"
                    placeholder="e.g. backend-api-service"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                  />
                </div>
                <div className="asset-form-group">
                  <label>Primary Language</label>
                  <select
                    className="asset-form-select"
                    value={projectLanguage}
                    onChange={(e) => setProjectLanguage(e.target.value)}
                  >
                    <option value="C">C</option>
                    <option value="C++">C++</option>
                    <option value="Java">Java</option>
                    <option value="Python">Python</option>
                    <option value="Go">Go</option>
                    <option value="JavaScript">JavaScript</option>
                  </select>
                </div>
                <div className="asset-form-group">
                  <label>Project Archive (.rar / .zip)</label>
                  <label className="asset-file-drop">
                    <input
                      type="file"
                      accept=".rar,.zip"
                      onChange={(e) => setUploadedFile(e.target.files)}
                    />
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px" }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--blue-500)" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                      </svg>
                      <span style={{ fontSize: "13px", color: "var(--fg-primary)", fontWeight: "500" }}>
                        {uploadedFile && uploadedFile[0] ? uploadedFile[0].name : "Choose .rar or .zip archive"}
                      </span>
                      <span style={{ fontSize: "11px", color: "var(--fg-tertiary)" }}>
                        Click to browse source repository package
                      </span>
                    </div>
                  </label>
                </div>
              </>
            )}
          </div>
          <div
            className="modal-footer"
            style={{
              backgroundColor: theme === "light" ? "#f8fafc" : "#141d30",
              borderTop: theme === "light" ? "1px solid #dfe5ee" : "1px solid #26324a",
              padding: "12px 20px",
            }}
          >
            <button
              type="button"
              className="asset-btn btn-secondary"
              onClick={() => {
                setAddAssetStep2Open(false);
                setAssetTypeModalOpen(true);
              }}
            >
              Back
            </button>
            <button
              type="button"
              className="asset-btn btn-primary"
              disabled={isSavingAsset}
              onClick={handleSaveAsset}
            >
              {isSavingAsset ? (
                <>
                  <Spinner size="sm" style={{ width: "12px", height: "12px" }} />
                  Saving...
                </>
              ) : (
                "Save Asset"
              )}
            </button>
          </div>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default AssetList;
